/**
 * Small pure helpers the sign-up and sign-in screens share: the product name, the domain shown
 * after a workspace name, a workspace address built from a company name, and a password meter.
 */

/** The name every pre-sign-in screen shows, so the logo, the address suffix and the Terms agree. */
export { PRODUCT_NAME } from '../../lib/brand.js';

import { SIGNUP_DIAL_CODES } from './signupPhone.js';

/**
 * Shown after the workspace name (`acme.peopleorbit.in`). Display only: where a company is really
 * reached is decided by `APP_DOMAIN` in lib/subdomain.ts; a production build bakes the same value
 * into `VITE_APP_DOMAIN`.
 */
export const WORKSPACE_DOMAIN: string = import.meta.env.VITE_APP_DOMAIN ?? 'peopleorbit.in';

const MAX_SLUG = 40;

/**
 * A workspace address from a company name: `Acme Technologies Pvt. Ltd.` becomes
 * `acme-technologies-pvt-ltd`. Lower case, letters and digits joined by single dashes, no dash at
 * either end. Returns '' when nothing usable is left, so the field stays empty rather than showing
 * a value the server would refuse.
 */
export function workspaceSlug(companyName: string): string {
  return companyName
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, MAX_SLUG)
    .replace(/-+$/, '');
}

/** What a person types into the address box, kept to what the server accepts. */
export function cleanSlugInput(typed: string): string {
  return typed
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 64);
}

export interface PasswordStrength {
  /** 0 nothing typed, 1 too short, 2 weak, 3 good, 4 strong. */
  level: 0 | 1 | 2 | 3 | 4;
  message: string;
}

export const PASSWORD_MIN_LENGTH = 8;

/** Patterns people reach for first; a password built on one is easy to guess whatever its length. */
const COMMON_PARTS = [
  'password',
  'passw0rd',
  'qwerty',
  'letmein',
  'welcome',
  'iloveyou',
  'admin',
  'abc123',
  'india123',
  '123456',
  '111111',
];

/**
 * How many characters carry information. A character that repeats the one before it or continues a
 * run ("aa", "abcd", "4321") adds nothing; and a password cannot be worth more than three times its
 * number of different characters, which stops "abababababab" passing on length alone.
 */
function effectiveLength(password: string): number {
  let informative = 1;
  for (let i = 1; i < password.length; i++) {
    if (Math.abs(password.charCodeAt(i) - password.charCodeAt(i - 1)) > 1) informative++;
  }
  return Math.max(1, Math.min(informative, new Set(password).size * 3));
}

/**
 * An advisory meter, not a rule: the only thing the server enforces is the 8-character floor.
 * Strength is length times the size of the character pool the password draws on (lower case,
 * upper case, digits, symbols), counting only characters that are not repeats or simple runs, and
 * capped when it is built on a common word. So `@Rakesh27` (nine different characters from all four
 * groups) is strong, while `abcdefghijkl` and `aaaaaaaaaaaa` are not.
 */
export function passwordStrength(password: string): PasswordStrength {
  if (!password) {
    return { level: 0, message: 'Use 8 or more characters.' };
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return { level: 1, message: 'Too short. Use at least 8 characters.' };
  }
  const lower = password.toLowerCase();
  if (COMMON_PARTS.some((part) => lower.includes(part))) {
    return { level: 2, message: 'Easy to guess. Avoid common words and patterns.' };
  }
  const pool =
    (/[a-z]/.test(password) ? 26 : 0) +
    (/[A-Z]/.test(password) ? 26 : 0) +
    (/\d/.test(password) ? 10 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 33 : 0);
  const bits = effectiveLength(password) * Math.log2(pool);
  if (bits >= 56) return { level: 4, message: 'Strong.' };
  if (bits >= 42) return { level: 3, message: 'Good. A longer password is stronger.' };
  return { level: 2, message: 'Weak. Add length, capitals, numbers or symbols.' };
}

/** Local part up to 64, whole address up to 254; no leading, trailing or doubled dots; labels without edge dashes; a letters-only TLD of 2+. */
const EMAIL_RE =
  /^(?!\.)(?!.*\.\.)(?!.*\.@)[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,24}$/;

/** Misspellings of the big providers, with the address they almost certainly meant. */
const DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cmo': 'gmail.com',
  'gmail.co': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'outlok.com': 'outlook.com',
};

/**
 * Why an email address is not usable, or null when it is. Format first, then two checks that catch
 * what a format cannot: a Gmail address has to follow Gmail's own username rules (6 to 30 letters,
 * digits or dots, ignoring a +tag), and a mistyped big-provider domain gets a "did you mean".
 */
export function emailProblem(email: string): string | null {
  const value = email.trim();
  if (!value) return 'Enter your work email.';
  if (value.length > 254 || !EMAIL_RE.test(value)) {
    return 'Enter a valid email address, like anita@acme.com.';
  }
  const at = value.lastIndexOf('@');
  const local = value.slice(0, at);
  const domain = value.slice(at + 1).toLowerCase();
  const fix = DOMAIN_TYPOS[domain];
  if (fix) return `Did you mean ${local}@${fix}?`;
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    const username = (local.split('+')[0] ?? '').toLowerCase();
    if (!/^(?!\.)(?!.*\.\.)(?!.*\.$)[a-z0-9.]{6,30}$/.test(username)) {
      return 'Gmail addresses have 6 to 30 letters, numbers or dots. Check for typos.';
    }
  }
  return null;
}

/**
 * True when a sign-in identifier is a mobile number rather than an email: only digits and the
 * separators people type (`+91 98765-43210`, `(0) 98765 43210`), with 7 to 15 digits.
 */
export function looksLikePhone(identifier: string): boolean {
  const value = identifier.trim();
  if (!/^\+?[\d\s\-.()]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

/**
 * The sign-in identifier as sent: an email as typed (the contract trims and lowercases it), a mobile
 * number with its separators dropped and a `00` prefix written `+`. A number of 12 or more digits
 * that starts with a calling code sign-up knows (`919876543210`) is that code without its `+`, and
 * is sent as `+919876543210`. A shorter one (a 10-digit national number, or one with its trunk `0`)
 * is left as digits: the API reads it in the company's own country (loginRequestSchema's `email`
 * doc comment).
 */
export function loginIdentifier(identifier: string): string {
  const value = identifier.trim();
  if (!looksLikePhone(value)) return value;
  const digits = value.replace(/\D/g, '');
  if (value.startsWith('+')) return `+${digits}`;
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  if (digits.length >= 12 && SIGNUP_DIAL_CODES.some((country) => digits.startsWith(country.code))) {
    return `+${digits}`;
  }
  return digits;
}

/** Why a sign-in identifier (email or mobile number) cannot be sent yet, or null. */
export function loginIdentifierProblem(identifier: string): string | null {
  const value = identifier.trim();
  if (!value) return 'Enter your work email or mobile number.';
  if (looksLikePhone(value)) return null;
  if (/^[\d\s+\-.()]+$/.test(value)) return 'Enter your full mobile number, or your email.';
  return emailProblem(value);
}

/**
 * The first problem the API reported, with the field's on-screen name in front of it
 * ("Workspace address: Must be a lowercase slug"). The API's own 422 text is the generic
 * "Invalid request body"; the reason sits in `details.fieldErrors`. Null when there is none.
 */
export function firstFieldError(error: unknown, labels: Record<string, string>): string | null {
  const body = (error as { body?: { details?: { fieldErrors?: unknown } } } | undefined)?.body;
  const fieldErrors = body?.details?.fieldErrors;
  if (!fieldErrors || typeof fieldErrors !== 'object') return null;
  for (const [field, messages] of Object.entries(fieldErrors as Record<string, unknown>)) {
    const message = Array.isArray(messages) ? messages[0] : undefined;
    if (typeof message === 'string' && message) {
      const label = labels[field];
      return label ? `${label}: ${message}` : message;
    }
  }
  return null;
}

/** What to say when the API rejects the form but gives no reason we can name. */
export const GENERIC_VALIDATION_MESSAGE =
  'Some details look wrong. Check each field and try again.';
